import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FwTooltipDirective } from '@flywheel-io/vision';
import orderBy from 'lodash/orderBy';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import { debounceTime, first, map, switchMap, tap } from 'rxjs/operators';

import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { ApiParams, CoreApiService } from '../../core-api.service';
import { ProfileService } from '../../profile/profile.service';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { getOriginInfo, OriginInfo } from '../../shared/file.utils';
import { Acquisition } from '../../shared/models/acquisition.model';
import { File } from '../../shared/models/file.model';
import { Session } from '../../shared/models/session.model';
import { replayValue } from '../../shared/observable.utils';
import { ViewerParameters } from '../../viewers/frame/viewer-frame.model';
import { SessionDataState } from '../session-data/session-data.component';

interface FilterFields {
  fileName: string;
  fileType: string;
}

interface BidsBucket {
  label: string;
  files: File[];
}

@Component({
  selector: 'bids-files',
  templateUrl: './bids-files.component.html',
  styleUrls: ['./bids-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    DragDropModule,
    FileModule,
    FormsModule,
    FwTooltipDirective,
    ReactiveFormsModule,
    MaterialModule,
    PipesModule,
    SharedModule,
    RouterModule,
  ],
})
export class BidsFilesComponent implements OnChanges, OnDestroy {
  private coreApi = inject(CoreApiService);
  private profile = inject(ProfileService);
  private state = inject(SessionDataState);

  @Input() collectionId?: string;
  @Input() session: Session;

  bidsBuckets$: Observable<BidsBucket[]>;
  error: Error = null;
  fileParents = new WeakMap<File, Acquisition>();
  fileTypes: string[] = [];
  fileViewParameters: ViewerParameters = {};
  filterFields = new FormGroup({
    fileName: new FormControl(''),
    fileType: new FormControl(''),
  });
  sessionFiles$ = new ReplaySubject<File[]>(1);

  private subscriptions = {
    acquisitions: Subscription.EMPTY,
  };
  private origins = new WeakMap<File, OriginInfo>();

  constructor() {
    this.bidsBuckets$ = combineLatest([
      this.sessionFiles$,
      replayValue(this.filterFields).pipe(debounceTime(200)),
    ]).pipe(
      map(([sessionFiles, filterFields]: [File[], FilterFields]) => {
        if (!sessionFiles) {
          return null;
        }
        const buckets = {
          anat: [],
          beh: [],
          dwi: [],
          fmap: [],
          func: [],
          sourcedata: [],
        };
        const nonBids = [];
        const fileNameFilter = filterFields.fileName.toLowerCase();
        const fileTypeFilter = filterFields.fileType.toLowerCase();
        for (const file of sessionFiles) {
          const bids = file.info?.BIDS;
          const filename = String(bids?.Filename || file.name || '');
          if (!filename.toLowerCase().includes(fileNameFilter)) {
            continue;
          }
          if (fileTypeFilter && (file.type || '').toLowerCase() !== fileTypeFilter) {
            continue;
          }
          if (bids?.ignore || bids?.Ignore) {
            nonBids.push(file);
          } else if (bids?.Folder) {
            const folderFiles = buckets[bids.Folder] || [];
            folderFiles.push(file);
            buckets[bids.Folder] = folderFiles;
          } else {
            nonBids.push(file);
          }
        }
        return Object.entries(buckets)
          .map(([label, files]) => ({ label, files }))
          .concat({ label: 'nonBids', files: nonBids });
      }),
      tap((buckets) => {
        const fileIds = (buckets || [])
          .reduce((allFiles, bucket) => allFiles.concat(bucket.files), [])
          .map((file) => file._id);
        this.fileViewParameters = { fileIds };
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.session &&
      this.session?._id &&
      (changes.session.firstChange || this.session._id !== changes.session.previousValue?._id)
    ) {
      this.loadFiles();
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  getFileOrigin(file: File): OriginInfo {
    if (!this.origins.has(file)) {
      const acquisition = this.fileParents.get(file);
      this.origins.set(file, getOriginInfo({ ...file, parent: acquisition }, acquisition['join-origin'], true));
    }
    return this.origins.get(file);
  }

  isQuarantined(file: File): boolean {
    return file.virus_scan?.state === 'quarantined';
  }

  loadFiles(): void {
    this.subscriptions.acquisitions.unsubscribe();
    this.error = null;
    this.sessionFiles$.next(null);
    const params: ApiParams = {
      join: 'origin',
    };
    if (this.collectionId) {
      params.filter = `collections=${this.collectionId}`;
    }
    this.subscriptions.acquisitions = this.profile
      .getProfile()
      .pipe(
        first((data) => Boolean(data)),
        switchMap((profile) => {
          if (SiteRole.isSiteAdmin(profile)) {
            params.exhaustive = true;
          }
          return this.coreApi.sessions.acquisitions(this.session._id, params);
        }),
      )
      .subscribe({
        next: (response) => {
          const acquisitions = response.results;
          const dicomAcquisitions = new Set<Acquisition>();
          const sessionFiles = acquisitions.reduce((files, acq) => {
            for (const file of acq.files) {
              this.fileParents.set(file, acq);
              if (file.type === 'dicom') {
                dicomAcquisitions.add(acq);
              }
            }
            return files.concat(acq.files);
          }, []);
          this.sessionFiles$.next(sessionFiles);
          this.state.dicomAcquisitions$.next(Array.from(dicomAcquisitions));
          const fileTypes = new Set<string>(sessionFiles.map((file) => file.type || ''));
          this.fileTypes = orderBy(Array.from(fileTypes), (str) => str.toLowerCase(), 'asc');
        },
        error: (error) => {
          this.error = error;
        },
      });
  }
}
